'use client'

import { CertificationBadge } from '@/components/certification-badge'
import {
  ChevronDown,
  Launch as ExternalLink,
  LogoGithub as Github,
  Earth as Globe2,
  LogoInstagram as Instagram,
  LogoLinkedin as Linkedin,
  Screen as MonitorCog,
  Moon,
  Sun,
  LogoX as Twitter,
  LogoYoutube as Youtube,
} from '@carbon/icons-react'
import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import { useTheme } from 'next-themes'
import { useMounted } from '@/lib/opendocs/hooks/use-mounted'
import { useState, useEffect, useRef, useCallback } from 'react'

import { Button } from '@/components/ui/button'
import { useThemeSweep } from '@/components/theme-sweep-provider'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { ThemeLogo } from '@/components/theme-logo'
import { CTASection } from '@/components/cta-section'
import { AiSummary } from '@/components/ai-summary'
import { siteConfig } from '@/config/site'

// ─── Types ────────────────────────────────────────────────────────────────────

type linkType = 'DROPDOWN' | 'LINK' | 'EXTERNAL_LINK'

interface NavigationLink {
  type?: linkType
  name: string
  href?: string
  links?: Omit<NavigationLink, 'type'>[]
}

interface NavigationSection {
  title: string
  links: NavigationLink[]
}

// ─── Navigation Data ──────────────────────────────────────────────────────────

const NAVIGATION: Array<NavigationSection> = [
  {
    title: 'Solutions',
    links: [
      { type: 'LINK', name: 'IntelQI Product Suite', href: '/products' },
      {
        type: 'LINK',
        name: 'Cloud Infrastructure',
        href: '/solutions/cloud-infrastructure',
      },
      {
        type: 'LINK',
        name: 'AI Infrastructure',
        href: '/solutions/ai-infrastructure',
      },
      { type: 'LINK', name: 'Unified Digital Campus', href: '/products/udc' },
      { type: 'LINK', name: 'HR Management System', href: '/products/hrms' },
      { type: 'LINK', name: 'Payroll Management', href: '/products/payroll' },
      { type: 'LINK', name: 'Accounting System', href: '/products/accounts' },
      {
        type: 'LINK',
        name: 'Learning Management System',
        href: '/products/lms',
      },
      {
        type: 'LINK',
        name: 'School Management System',
        href: '/schoolerp',
      },
    ],
  },
  {
    title: 'Resources',
    links: [
      { type: 'LINK', name: 'Blogs', href: '/blogs' },
      { type: 'LINK', name: 'Product Brochure', href: '/product-brochure' },
      { type: 'LINK', name: 'Product Updates', href: '/product-updates' },
    ],
  },
  {
    title: 'Company',
    links: [
      { type: 'LINK', name: 'About Us', href: '/about-us' },
      // { type: "LINK", name: "Careers", href: "/careers" },
      // { type: "LINK", name: "Leadership", href: "/about-us/team" },
      { type: 'LINK', name: 'Customer Stories', href: '/customer-stories' },
      {
        type: 'DROPDOWN',
        name: 'Legal',
        links: [
          { name: 'Privacy Policy', href: '/privacy' },
          { name: 'Terms of Service', href: '/terms' },
          { name: 'Cookie Policy', href: '/cookies' },
          // { name: "Accessibility", href: "/accessibility" },
        ],
      },
    ],
  },
]

const SOCIAL_LINKS = [
  { name: 'LinkedIn', href: 'https://www.linkedin.com/company/euclideum/' },
  {
    name: 'Glassdoor',
    href: 'https://www.glassdoor.co.in/Overview/Working-at-EUCLIDEUM-SOLUTIONS-EI_IE10895753.11,30.htm',
  },
  { name: 'GitHub', href: 'https://github.com/IntelQI' },
  { name: 'Medium', href: 'https://intelqi.medium.com' },
]

interface FooterDropdownProps {
  name: string
  links: Omit<NavigationLink, 'type'>[]
  mounted?: boolean
}

function FooterDropdown({ name, links, mounted }: FooterDropdownProps) {
  const router = useRouter()
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)

  // Close on outside pointer-down (capture phase beats React bubbling)
  useEffect(() => {
    if (!open) return
    const onOutside = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('pointerdown', onOutside, true)
    return () => document.removeEventListener('pointerdown', onOutside, true)
  }, [open])

  // Close on Escape
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  const navigate = useCallback(
    (href: string) => {
      // 1. Synchronously close the dropdown so it unmounts before navigation
      setOpen(false)
      // 2. Push route WITHOUT scroll restoration
      //    scroll:false tells Next.js NOT to run its scroll-restoration logic
      router.push(href, { scroll: false })
      // 3. Immediately jump to top of the incoming page
      window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior })
    },
    [router]
  )

  return (
    <div ref={wrapRef} className="relative">
      {/* Trigger button */}
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="
          inline-flex cursor-pointer items-center gap-0.5
          text-xs leading-5 text-muted-foreground
          hover:text-foreground
          dark:text-muted-foreground dark:hover:text-foreground
          sm:text-sm
          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1
        "
      >
        {name}
        {mounted && (
          <ChevronDown
            aria-hidden="true"
            className={`ml-0.5 size-3 shrink-0 transition-transform duration-200 sm:size-3.5 ${open ? 'rotate-180' : ''}`}
          />
        )}
      </button>

      {/* Inline panel never portalled */}
      {open && (
        <ul
          // title='list'
          role="listbox"
          className="
            absolute left-0 z-50 mt-1.5
            w-44 rounded-lg
            border border-border bg-card
            shadow-lg
            dark:border-border dark:bg-background
          "
        >
          {links.map((link) => (
            <li key={link.name}>
              <button
                type="button"
                onPointerDown={(e) => {
                  e.stopPropagation() // don't let document listener see this
                  navigate(link.href!)
                }}
                className="
        block w-full cursor-pointer rounded-lg
        px-3 py-2 text-left
        text-xs text-muted-foreground
        hover:bg-muted
        dark:text-muted-foreground dark:hover:bg-muted dark:hover:text-foreground
        sm:text-sm
        focus-visible:outline-none focus-visible:bg-muted
      "
              >
                {link.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// ─── getLink ──────────────────────────────────────────────────────────────────

function getLink({
  type,
  href,
  name,
  links,
  mounted,
}: NavigationLink & { mounted?: boolean }) {
  if (type === 'EXTERNAL_LINK') {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex cursor-pointer items-center gap-0.5 text-xs leading-5 text-muted-foreground hover:text-foreground dark:text-muted-foreground dark:hover:text-foreground sm:text-sm"
      >
        {name}
        {mounted && (
          <ExternalLink
            className="ml-0.5 size-3 sm:size-3.5"
            aria-hidden="true"
          />
        )}
      </a>
    )
  }

  if (type === 'DROPDOWN') {
    return <FooterDropdown name={name} links={links ?? []} mounted={mounted} />
  }

  return (
    <Link
      href={href!}
      className="cursor-pointer text-xs leading-5 text-muted-foreground hover:text-foreground dark:text-muted-foreground dark:hover:text-foreground sm:text-sm"
    >
      {name}
    </Link>
  )
}

// ─── SiteFooter ───────────────────────────────────────────────────────────────

export function SiteFooter() {
  const { theme, setTheme, resolvedTheme, systemTheme } = useTheme()
  const { sweepTheme } = useThemeSweep()
  const mounted = useMounted()
  const pathname = usePathname()
  const currentYear = new Date().getFullYear()
  const isBlogPage = pathname?.startsWith('/blog')

  const onThemeChange = (v: 'light' | 'dark' | 'system') => {
    if (!v || v === theme) return
    // Same foil sweep as the header toggle — but only when the visible
    // appearance actually flips. Picking a value that resolves to the current
    // look (e.g. "system" on a light OS while already on "light") just
    // commits the preference quietly; a full-screen curtain over a no-op
    // would read as the switch doing nothing.
    const next = v === 'system' ? systemTheme : v
    if (next && next !== resolvedTheme) sweepTheme(() => setTheme(v))
    else setTheme(v)
  }

  return (
    <>
      {!isBlogPage && <CTASection />}

      <section className="bg-background px-3 pb-4 pt-2 sm:px-5 sm:pb-6">
        <footer className="mx-auto w-full max-w-[1800px] rounded-3xl border border-border/70 bg-background px-5 py-8 sm:rounded-[2rem] sm:px-8 sm:py-10 lg:px-12">
          <div className="flex w-full flex-col gap-6">
            {/* ── Navigation grid ────────────────────────────────────────────
              < 480 px  (xs/mobile) : 2 columns
                480 px  (sm)        : 3 columns
                768 px  (md)        : 4 columns + auto logo column
            ─────────────────────────────────────────────────────────────── */}
            <div className="grid w-full grid-cols-2 gap-x-4 gap-y-7 min-[480px]:grid-cols-3 md:grid-cols-[repeat(4,1fr)_auto] md:items-start md:gap-x-6 md:gap-y-0">
              {NAVIGATION.map((section) => (
                <div key={section.title}>
                  <h2 className="font-heading mb-2 text-xs font-semibold text-foreground dark:text-foreground sm:mb-3 sm:text-sm md:text-base">
                    {section.title}
                  </h2>
                  <ul className="space-y-1.5">
                    {section.links.map((link, i) => (
                      <li key={`${link.name}-${i}`}>
                        {getLink({
                          name: link.name,
                          href: link.href,
                          type: link.type,
                          links: link.links,
                          mounted,
                        })}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}

              {/* Socials */}
              <div>
                <h2 className="font-heading mb-2 text-xs font-semibold text-foreground dark:text-foreground sm:mb-3 sm:text-sm md:text-base">
                  Socials
                </h2>
                <ul className="space-y-1.5" suppressHydrationWarning>
                  {SOCIAL_LINKS.map((link, i) => (
                    <li key={i} suppressHydrationWarning>
                      <a
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="cursor-pointer text-xs leading-5 text-muted-foreground hover:text-foreground dark:text-muted-foreground dark:hover:text-foreground sm:text-sm"
                        suppressHydrationWarning
                      >
                        {link.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Logo spans 2 cols on mobile, 1 col on sm, fixed top-right on md+ */}
              <Link
                href="/"
                className="col-span-2 min-[480px]:col-span-1 md:col-[5/6] md:row-[1] md:self-start md:justify-self-end"
              >
                <ThemeLogo
                  width={36}
                  className="size-9 object-cover object-center"
                />
              </Link>
            </div>

            {/* ── Bottom bar ──────────────────────────────────────────────── */}
            <div className="mt-2 flex w-full flex-wrap items-center justify-between gap-3">
              {/* Status indicator */}
              <Button
                asChild
                variant="ghost"
                className="h-auto px-2 py-1 text-foreground hover:text-foreground dark:text-foreground dark:hover:text-foreground"
              >
                <a
                  href="https://cloud.umami.is/share/cuelAuDLqEqz1fad"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-xs sm:gap-3 sm:text-sm"
                >
                  <span className="relative size-[0.4375rem] shrink-0">
                    <span className="absolute left-1/2 top-1/2 z-10 size-[0.6875rem] -translate-x-1/2 -translate-y-1/2 animate-pulse rounded-full bg-emerald-600/50 dark:bg-primary-active/50" />
                    <span className="absolute left-1/2 top-1/2 z-20 size-full -translate-x-1/2 -translate-y-1/2 rounded-full bg-emerald-600 dark:bg-primary-active" />
                  </span>
                  All systems operational
                </a>
              </Button>

              {/* Right group: certifications + theme switcher */}
              <div className="flex flex-wrap items-center gap-4 sm:gap-7">
                {/* ISO certifications, as ring tiles. Only the two standards
                    Euclideum actually holds are shown — the footer is not the
                    place to imply an audit we have not had. The /architecture
                    page still uses the original marks. */}
                <div className="flex items-center gap-3">
                  <CertificationBadge
                    standard="9001"
                    title="ISO 9001:2015 certified (Quality Management)"
                  />
                  <CertificationBadge
                    standard="27001"
                    title="ISO 27001:2022 certified (Information Security)"
                  />
                </div>

                {/* Theme switcher */}
                <ToggleGroup
                  value={theme}
                  onValueChange={onThemeChange}
                  type="single"
                  className="rounded-full border border-border dark:border-border"
                  suppressHydrationWarning
                >
                  {(
                    [
                      { value: 'system', Icon: MonitorCog, label: 'system' },
                      { value: 'light', Icon: Sun, label: 'light' },
                      { value: 'dark', Icon: Moon, label: 'dark' },
                    ] as const
                  ).map(({ value, Icon, label }) => (
                    <ToggleGroupItem
                      key={value}
                      value={value}
                      aria-label={`Toggle ${label} theme`}
                      className="size-6 rounded-full p-0 data-[state=on]:bg-accent"
                      suppressHydrationWarning
                    >
                      {mounted && (
                        <Icon
                          className="size-3 text-muted-foreground dark:text-foreground"
                          aria-hidden="true"
                        />
                      )}
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>
              </div>
            </div>

            {/* ── Copyright / Tagline ─────────────────────────────────────── */}
            <div className="flex flex-col gap-4 border-t border-border pt-5 dark:border-border">
              <AiSummary />

              <div className="flex flex-col items-start justify-between gap-2 sm:flex-row sm:items-start sm:gap-4">
                {/* Copyright */}
                <p className="text-xs text-muted-foreground dark:text-muted-foreground sm:text-sm">
                  © {currentYear} {siteConfig.company.legalName}. All rights
                  reserved.
                </p>

                <p
                  className="block px-2 py-1 text-[0.65rem] leading-normal text-muted-foreground dark:text-muted-foreground min-[480px]:text-xs sm:text-sm md:text-base"
                  aria-label={siteConfig.company.tagline}
                >
                  Transform Ideas Into Impact
                </p>
              </div>
            </div>
          </div>
        </footer>
      </section>
    </>
  )
}
